import { useRef, useState, type FormEvent } from "react";
import { ArrowUpRight } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import { DemoNote } from "./Flows";
import { ROOFING_BASE } from "./Site";
import { contactTopics, leadSources, roofContactInputSchema, useRoofing } from "./state";
import "./contact.css";

export function ContactPage() {
  const { createRequest, ready } = useRoofing();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const errorMessage = useRef<HTMLParagraphElement>(null);
  const requestedTopic = search.get("topic") ?? "estimate";
  const topic = Object.hasOwn(contactTopics, requestedTopic) ? requestedTopic : "estimate";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const result = roofContactInputSchema.safeParse({
      kind: "contact", topic: data.get("topic"),
      name: data.get("name"), email: data.get("email"), phone: data.get("phone"),
      zip: data.get("zip"), details: data.get("details"),
      source: leadSources.find(value => value === search.get("source")) ?? "website",
    });
    if (result.success) {
      const id = createRequest(result.data);
      if (id) {
        navigate(`${ROOFING_BASE}/project?request=${encodeURIComponent(id)}&created=1`);
        return;
      }
    }
    setError(result.success ? "Please check the details and try again." : result.error.issues[0].message);
    requestAnimationFrame(() => errorMessage.current?.focus());
  }

  return <section className="rr-width rr-contact">
    <div className="rr-contact-intro">
      <h1>Let’s talk<br />about your roof.</h1>
      <p>Request a free estimate, ask about a repair or get help with an existing project.</p>
    </div>
    <form className="rr-form rr-contact-form" aria-labelledby="roof-contact-heading" onSubmit={submit}>
      <h2 id="roof-contact-heading">How can we help?</h2>
      <div className="rr-two-fields">
        <label>Full name<input name="name" autoComplete="name" required maxLength={100} placeholder="Your name" /></label>
        <label>Email<input name="email" type="email" autoComplete="email" required maxLength={200} placeholder="you@example.com" /></label>
      </div>
      <div className="rr-two-fields">
        <label>Phone (optional)<input name="phone" type="tel" autoComplete="tel" maxLength={40} placeholder="Your phone number" /></label>
        <label>ZIP code<input name="zip" autoComplete="postal-code" inputMode="numeric" pattern="[0-9]{5}" required maxLength={5} placeholder="Property ZIP code" /></label>
      </div>
      <label>I’m getting in touch about…<select key={topic} name="topic" defaultValue={topic}>{Object.entries(contactTopics).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label>Tell us a little more<textarea name="details" required minLength={3} maxLength={1500} rows={4} placeholder="What would you like us to know about your roof or project?" /></label>
      <p className="rr-contact-note">An estimate starts with a review of the roof and the work involved.</p>
      {error && <p className="rr-error" role="alert" ref={errorMessage} tabIndex={-1}>{error}</p>}
      <button className="rr-button" type="submit" disabled={!ready}>Send request<ArrowUpRight size={18} aria-hidden /></button>
      <p className="rr-contact-note">We’ll use these details to respond to your request.</p>
      <DemoNote />
    </form>
    <div className="rr-contact-next">
      <h2>What happens next</h2>
      <ol>
        <li><strong>We review your request.</strong><p>A few details help us understand what you need.</p></li>
        <li><strong>We talk through the next step.</strong><p>Discuss the property, your question or a suitable visit.</p></li>
        <li><strong>You know where things stand.</strong><p>Review the scope and estimate before deciding on proposed work.</p></li>
      </ol>
    </div>
  </section>;
}
